/**
 * useReservationDetail — TanStack Query hook over
 * `GET /organizations/{slug}/reservations/{id}` (SCRUM-335).
 *
 * Mirrors `use-reservations-list.ts` exactly, with a FIFTH branch: the
 * detail 404s on an absent OR cross-org reservation (anti-enumeration), a
 * distinct, non-error state the page renders as a "not found" panel — NOT
 * the generic Retry affordance. The full status union is
 * `loading | ready | forbidden | notFound | error`.
 *
 * Sentinels: the queryFn returns `FORBIDDEN` on 403 and `NOT_FOUND` on 404
 * (own module-level Symbols, distinct from the list hook's private
 * `FORBIDDEN`). Everything else non-ok / undefined-body throws → `error`.
 *
 *   - reads the org slug via `useCurrentOrg()` (RLS-scoped per CLAUDE.md
 *     non-negotiable §1)
 *   - `queryKey = [...bookingQueryKeys.detail(id), orgSlug]`
 *   - `enabled: orgSlug !== null && reservationId != null`
 *   - `retry: false`
 *   - the response envelope is `{ data: ReservationDetailDto }` — the
 *     `ready` result unwraps `result.data.data`
 *   - every branch carries a `refetch` function
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import type { ReservationDetailDto } from './types.js';
import { bookingQueryKeys } from './queryKeys.js';

/**
 * 403 sentinel — the caller lost `booking.reservations.read` since
 * `/auth/me` cached. The page renders a permission-denied panel (no Retry).
 */
export const FORBIDDEN = Symbol('reservation-detail:forbidden');

/**
 * 404 sentinel — the reservation is absent OR belongs to another org
 * (the BE returns 404 for both, anti-enumeration). A distinct, non-error
 * state: the page renders a "not found" panel.
 */
export const NOT_FOUND = Symbol('reservation-detail:notFound');

export type UseReservationDetailResult =
  | {
      readonly status: 'loading';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      /** The reservation detail straight from the unwrapped envelope. */
      readonly data: ReservationDetailDto;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'notFound';
      readonly data?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly data?: undefined;
      readonly refetch: () => void;
    };

export function useReservationDetail(
  reservationId: string | null,
): UseReservationDetailResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    // `detail(id)` requires a string; when id is null the query is disabled
    // so the key is never used — an empty string keeps the tuple type stable.
    queryKey: [...bookingQueryKeys.detail(reservationId ?? ''), orgSlug] as const,
    enabled: orgSlug !== null && reservationId != null,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/reservations/{id}',
        {
          params: {
            path: { slug: orgSlug as string, id: reservationId as string },
          },
        },
      );
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (result.response.status === 404) {
        return NOT_FOUND;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/reservations/${reservationId} failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || reservationId == null || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }
  if (query.data === NOT_FOUND) {
    return { status: 'notFound', refetch };
  }

  const envelope = query.data;
  return {
    status: 'ready',
    data: envelope.data,
    refetch,
  };
}
